<template>
    <div class="admin-settings-goals" data-test-view="goals">
        <l-module-access
            :module="'goals'"
            :module-access="team.moduleAccess"
            :title="$t(`adminSettings.goalsHeadlineTitle`)"
        ></l-module-access>

        <hr />

        <l-headline
            :title="$t(`adminSettings.customizationHeadlineTitle`)"
            :sub-title="$t(`adminSettings.customizationHeadlineSubtitle`)"
        ></l-headline>

        <label class="color-dark" for="settings-indirectReportsVisible-check">
            <input
                id="settings-indirectReportsVisible-check"
                v-model="team.settings.indirectReportsVisible"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-goals-indirect-reports-visible"
            />
            {{ $t(`adminSettings.goalsIndirectReportsVisibleLabel`) }}
            <span
                v-b-popover.hover="{
                    content: $t(`adminSettings.goalsIndirectReportsVisiblePopover`),
                }"
                class="l-default-margin"
            >
                <i class="fal fa-question-circle"></i>
            </span>
        </label>
        <br />

        <label class="color-dark" for="settings-adminsCanAccessAllGoals-check">
            <input
                id="settings-adminsCanAccessAllGoals-check"
                v-model="team.settings.adminsCanAccessAllGoals"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-goals-admin-access-all"
            />
            {{ $t(`adminSettings.goalsAdminsCanAccessAllGoalsLabel`) }}
            <span
                v-b-popover.hover="{
                    content: $t(`adminSettings.goalsAdminsCanAccessAllGoalsPopover`),
                }"
                class="l-default-margin"
            >
                <i class="fal fa-question-circle"></i>
            </span>
        </label>

        <l-sub-headline :text="$t(`adminSettings.goalsGoalCreationSubheading`)" />

        <label class="color-dark" for="settings-goalsManagedDisabled-check">
            <input
                id="settings-goalsManagedDisabled-check"
                v-model="team.settings.goalsManagedDisabled"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-goals-managed-disabled"
            />
            {{ $t(`adminSettings.goalsGoalsManagedDisabledLabel`) }}
            <span
                v-b-popover.hover="{
                    content: $t(`adminSettings.goalsGoalsManagedDisabledPopover`),
                }"
                class="l-default-margin"
            >
                <i class="fal fa-question-circle"></i>
            </span>
        </label>
        <br />

        <label class="color-dark" for="settings-goalTreeEnabled-check">
            <input
                id="settings-goalTreeEnabled-check"
                v-model="team.settings.goalTreeEnabled"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-goals-tree"
            />
            {{ $t(`adminSettings.goalsGoalTreeEnabledLabel`) }}
        </label>
        <br />

        <label class="color-dark" for="settings-usersCanCreateTeamGoals-check">
            <input
                id="settings-usersCanCreateTeamGoals-check"
                v-model="team.settings.usersCanCreateTeamGoals"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-goals-users-create-team-goals"
            />
            {{ $t(`adminSettings.goalsUsersCanCreateTeamGoalsLabel`) }}
            <span
                v-b-popover.hover="{
                    content: $t(`adminSettings.goalsUsersCanCreateTeamGoalsPopover`),
                }"
                class="l-default-margin"
            >
                <i class="fal fa-question-circle"></i>
            </span>
        </label>
        <br />

        <label class="color-dark" for="settings-goalTagsMandatory-check">
            <input
                id="settings-goalTagsMandatory-check"
                v-model="team.settings.goalTagsMandatory"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-goals-tags-mandatory"
            />
            {{ $t(`adminSettings.goalsGoalTagsMandatoryLabel`) }}
        </label>
        <br />

        <label class="color-dark" for="settings-goalInitiativesDisabled-check">
            <input
                id="settings-goalInitiativesDisabled-check"
                v-model="team.settings.goalInitiativesDisabled"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-goal-initiatives-disabled"
            />
            {{ $t(`adminSettings.goalsGoalInitiativesDisabled`) }}
        </label>
        <br />

        <label class="color-dark" for="settings-goalSkillsDisabled-check">
            <input
                id="settings-goalSkillsDisabled-check"
                v-model="team.settings.goalSkillsDisabled"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-goal-skills-disabled"
            />
            {{ $t(`adminSettings.goalsGoalSkillsDisabled`) }}
        </label>

        <l-sub-headline :text="$t(`adminSettings.goalsGoalVisibilitySubheading`)" />

        <label class="color-dark" for="settings-goalsPublicByDefault-check">
            <input
                id="settings-goalsPublicByDefault-check"
                v-model="team.settings.goalsPublicByDefault"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-goals-public-default"
            />
            {{ $t(`adminSettings.goalsGoalsPublicByDefaultLabel`) }}
        </label>
        <br />

        <label class="color-dark" for="settings-goalsDeactivatePublic-check">
            <input
                id="settings-goalsDeactivatePublic-check"
                v-model="team.settings.goalsDeactivatePublic"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-goals-deactivate-public"
            />
            {{ $t(`adminSettings.goalsGoalsDeactivatePublicLabel`) }}
        </label>

        <hr />

        <div v-show="updateSuccess">
            <l-text-alert
                type="success"
                :text="$t(`adminSettings.goalsUpdateSuccess`)"
                data-test-id="team-settings-goals-update-success"
            />
        </div>
        <div v-show="updateError">
            <l-text-alert
                type="warning"
                :text="$t(`adminSettings.goalsUpdateError`)"
                data-test-id="team-settings-goals-update-error"
            />
        </div>
        <div class="row">
            <div class="col-md-offset-8 col-md-4">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    :disabled="!rights.A_PERM"
                    data-test-id="team-settings-goals-update"
                    @click="updateGlobalTeam()"
                >
                    {{ $t(`adminSettings.goalsUpdateBtn`) }}
                </button>
            </div>
        </div>

        <hr />

        <!-- GOAL TAGS -->
        <l-headline
            :title="$t(`adminSettings.goalsTagsHeadlineTitle`)"
            :sub-title="$t(`adminSettings.goalsTagsHeadlineSubtitle`)"
        ></l-headline>

        <draggable v-model="tags" handle=".input-group-addon" @change="changeTagOrder">
            <transition-group>
                <div
                    v-for="(tag, tagIndex) in tags"
                    :key="tag._id"
                    class="row"
                    data-test-class="team-settings-goals-tags-list-item"
                >
                    <div class="col-xs-12">
                        <div class="form-group">
                            <div class="input-group label-floating">
                                <l-draggable-icon class="input-group-addon" />

                                <l-input-text
                                    v-model="tag.name"
                                    :label="''"
                                    :disabled="!tag.editing"
                                    data-test-class="team-settings-goals-tags-name"
                                    :data-test-id="`team-settings-goals-tags-${tagIndex}-name`"
                                />

                                <span
                                    v-show="tag.status === 'viewOnly'"
                                    class="input-group-addon clickable color-primary"
                                    data-test-class="team-settings-goals-tags-view-only"
                                    :data-test-id="`team-settings-goals-tags-${tagIndex}-view-only`"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            tag.status = 'active';
                                            updateTag(tag);
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            tag.status = 'active';
                                            updateTag(tag);
                                        }
                                    "
                                >
                                    {{ $t(`adminSettings.goalsTagsViewOnly`) }}
                                </span>
                                <span
                                    v-show="tag.status === 'active'"
                                    class="input-group-addon clickable color-primary"
                                    data-test-class="team-settings-goals-tags-active"
                                    :data-test-id="`team-settings-goals-tags-${tagIndex}-active`"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            tag.status = 'viewOnly';
                                            updateTag(tag);
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            tag.status = 'viewOnly';
                                            updateTag(tag);
                                        }
                                    "
                                >
                                    {{ $t(`adminSettings.goalsTagsActive`) }}
                                </span>

                                <span
                                    v-show="tag.editing"
                                    class="input-group-addon clickable color-primary"
                                    data-test-class="team-settings-goals-tags-save"
                                    :data-test-id="`team-settings-goals-tags-${tagIndex}-save`"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            $set(tag, 'editing', false);
                                            updateTag(tag);
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            $set(tag, 'editing', false);
                                            updateTag(tag);
                                        }
                                    "
                                >
                                    {{ $t(`adminSettings.goalsTagsSave`) }}
                                </span>
                                <span
                                    v-show="!tag.editing"
                                    class="input-group-addon clickable color-primary"
                                    data-test-class="team-settings-goals-tags-edit"
                                    :data-test-id="`team-settings-goals-tags-${tagIndex}-edit`"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            $set(tag, 'editing', true);
                                            $forceUpdate();
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            $set(tag, 'editing', true);
                                            $forceUpdate();
                                        }
                                    "
                                >
                                    {{ $t(`adminSettings.goalsTagsEdit`) }}
                                </span>
                                <span
                                    class="input-group-addon clickable color-warning"
                                    data-test-class="team-settings-goals-tags-remove"
                                    :data-test-id="`team-settings-goals-tags-${tagIndex}-remove`"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            tag.status = 'removed';
                                            updateTag(tag);
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            tag.status = 'removed';
                                            updateTag(tag);
                                        }
                                    "
                                >
                                    {{ $t(`adminSettings.goalsTagsRemove`) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </transition-group>
        </draggable>

        <div class="row">
            <div class="col-xs-12">
                <div class="form-group">
                    <div class="input-group label-floating">
                        <l-input-text
                            v-model="newTagName"
                            :optional="true"
                            :label="''"
                            :placeholder="$t(`adminSettings.goalsTagsCreatePlaceholder`)"
                            :hide-help-block="true"
                            data-test-id="team-settings-goals-tags-new"
                        />
                        <span
                            class="input-group-addon clickable color-primary l-default-margin"
                            data-test-id="team-settings-goals-tags-new-create"
                            tabindex="0"
                            @click="
                                () => {
                                    updateTag({
                                        name: newTagName,
                                        status: 'active',
                                        order: tags.length
                                            ? (tags[tags.length - 1].order || tags.length) + 1
                                            : 0,
                                    });
                                    newTagName = '';
                                }
                            "
                            @keyup.enter="
                                () => {
                                    updateTag({
                                        name: newTagName,
                                        status: 'active',
                                        order: tags.length
                                            ? (tags[tags.length - 1].order || tags.length) + 1
                                            : 0,
                                    });
                                    newTagName = '';
                                }
                            "
                        >
                            {{ $t(`adminSettings.goalsTagsCreateBtn`) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <hr />

        <!-- Import Goals -->
        <l-headline :title="$t(`adminSettings.goalsImportHeadlineTitle`)"></l-headline>

        <form class="admin-settings-goals-import-form">
            <div class="text-center">
                <label
                    :class="[
                        'btn',
                        'btn-primary',
                        'btn-raised',
                        { disabled: uploadGoalInProgress },
                    ]"
                    for="import-goals-upload-file"
                >
                    <span v-show="!uploadGoalInProgress">
                        <i class="fa fa-upload"></i> {{ $t(`adminSettings.goalsImportUploadBtn`) }}
                    </span>
                    <span v-show="uploadGoalInProgress">
                        <i class="fa fa-refresh fa-spin"></i>
                        {{ $t(`adminSettings.goalsImportUploadInProgressBtn`) }}
                    </span>
                    <input
                        id="import-goals-upload-file"
                        type="file"
                        name="file"
                        accept=".csv,.xls,.xlsx"
                        style="display: none;"
                        @change="e => uploadGoals(e)"
                    />
                </label>
                <br />
                <p>
                    {{ $t(`adminSettings.goalsImportText`) }}
                    <a
                        href="https://docs.google.com/spreadsheets/d/1FmSIv8hORZWRMyfcyTM35bAB_9uhSVnpObO4klHyMP8/edit?usp=sharing"
                        target="_blank"
                        rel="noopener"
                    >
                        {{ $t(`adminSettings.goalsImportTextLink`) }}</a
                    >.
                </p>
            </div>
        </form>

        <div id="import-goals-result">
            <div v-if="uploadGoalResults.count">
                <hr />
                <p class="color-success">
                    <strong>{{
                        $tc(`adminSettings.goalsImportSuccessText`, uploadGoalResults.count, {
                            count: uploadGoalResults.count,
                        })
                    }}</strong>
                </p>
            </div>

            <div v-if="uploadGoalResults.errors.length">
                <hr />
                <p class="color-danger">
                    <strong>{{ $t(`adminSettings.goalsImportErrorText`) }}</strong>
                </p>
                <div v-for="error in uploadGoalResults.errors" :key="error.sheetRow" class="row">
                    <div class="col-md-1">
                        {{ $t(`adminSettings.goalsImportErrorTextRow`) }} {{ error.sheetRow }}
                    </div>
                    <div class="col-md-11">
                        {{ error.error }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import Draggable from "vuedraggable";

import LDraggableIcon from "@/components/LDraggableIcon";
import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LSubHeadline from "@/components/LSubHeadline";
import LTextAlert from "@/components/LTextAlert";
import LModuleAccess from "@/components/LModuleAccess";

export default {
    components: {
        Draggable,
        LDraggableIcon,
        LHeadline,
        LInputText,
        LSubHeadline,
        LTextAlert,
        LModuleAccess,
    },
    data() {
        return {
            team: {},
            updateSuccess: false,
            updateError: false,
            newTagName: "",
            uploadGoalInProgress: false,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            getTeamInfo: "teams/current/info",
            uploadGoalResults: "goals/upload/results",
            tags: "tags/tags/goals",
        }),
    },
    watch: {},
    created() {
        // Cloning the information from the store so we can modify it before updating the store,
        // when the user clicks on the update button.
        //
        this.team = this.getTeamInfo();
    },
    methods: {
        updateGlobalTeam() {
            this.updateSuccess = false;
            this.updateError = false;

            this.$store
                .dispatch({
                    type: "teams/current/update",
                    payload: {
                        scope: "settingsGoals",
                        update: {
                            indirectReportsVisible: this.team.settings.indirectReportsVisible,
                            adminsCanAccessAllGoals: this.team.settings.adminsCanAccessAllGoals,
                            usersCanCreateTeamGoals: this.team.settings.usersCanCreateTeamGoals,
                            goalsManagedDisabled: this.team.settings.goalsManagedDisabled,
                            goalsPublicByDefault: this.team.settings.goalsPublicByDefault,
                            goalsDeactivatePublic: this.team.settings.goalsDeactivatePublic,
                            goalTreeEnabled: this.team.settings.goalTreeEnabled,
                            goalTagsMandatory: this.team.settings.goalTagsMandatory,
                            goalWeightsEnabled: this.team.settings.goalWeightsEnabled,
                            goalInitiativesDisabled: this.team.settings.goalInitiativesDisabled,
                            goalSkillsDisabled: this.team.settings.goalSkillsDisabled,
                        },
                    },
                })
                .then(() => {
                    this.updateSuccess = true;
                })
                .catch(() => {
                    this.updateError = true;
                });
        },
        updateTag({ _id, name, status, order }) {
            if (!name) {
                return;
            }
            this.$store.dispatch({
                type: "tags/tags/update",
                payload: {
                    data: { _id, name, status, order, type: "goals" },
                },
            });
        },
        changeTagOrder(x) {
            const tags = [];
            tags.push(this.tags[x.moved.newIndex]);
            tags.push(this.tags[x.moved.oldIndex]);
            [tags[0].order, tags[1].order] = [tags[1].order, tags[0].order];
            this.tags[x.moved.oldIndex].order;
            this.$store.dispatch({
                type: "tags/tags/updateOrder",
                payload: {
                    data: tags,
                },
            });
        },
        uploadGoals(e) {
            const { files } = e.target;

            if (!files) {
                return;
            }

            const file = files[0];

            this.uploadGoalInProgress = true;

            this.$store.dispatch({ type: "goals/upload/upload", payload: { file } }).then(() => {
                this.uploadGoalInProgress = false;

                document.getElementById("import-goals-result").scrollIntoView({
                    behavior: "smooth",
                });
            });
        },
    },
};
</script>

<style scoped>
.admin-settings-goals-import-form .btn i {
    margin-right: 8px;
}
</style>
